/**
 * Authentication context provider and hook.
 *
 * Manages user state (JWT token + profile) in React context and
 * localStorage. Exposes login, register, and logout actions that
 * call the backend auth API and update local state atomically.
 *
 * @module AuthContext
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../api';
import i18n from '../i18n';

/** Authenticated user profile stored in context and localStorage. */
interface User {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  company?: string;
  preferred_language?: string;
}

/** Shape of the auth context value consumed by useAuth(). */
interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string, company?: string) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Provides authentication state and actions to the component tree.
 * Rehydrates session from localStorage on mount.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const savedToken = localStorage.getItem('access_token');
      const savedUser = localStorage.getItem('user');
      if (savedToken && savedUser) {
        const parsed = JSON.parse(savedUser);
        if (parsed?.user_id && parsed?.role) {
          setToken(savedToken);
          setUser(parsed);
        }
      }
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const res = await api.post('/api/auth/login', { email, password });
    const { access_token, user_id, role, full_name } = res.data;
    const userData: User = { user_id, email, role, full_name };
    setToken(access_token);
    setUser(userData);
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const register = async (email: string, password: string, fullName: string, company?: string) => {
    const res = await api.post('/api/auth/register', {
      email,
      password,
      full_name: fullName,
      company,
      preferred_language: i18n.language,
    });
    const { access_token, user_id, role, full_name } = res.data;
    const userData: User = { user_id, email, role, full_name };
    setToken(access_token);
    setUser(userData);
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider
      value={{ user, token, loading, login, register, logout, isAdmin: user?.role === 'admin' }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Convenience hook that returns the current auth context.
 * Must be called inside an AuthProvider.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
